/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from 'react';
import videojs from 'video.js';
import { useRouter } from 'next/router';
import { nonPageTracker } from '@src/adobe-launch/data-layer-provider';
import { TrackingEvent } from '@src/adobe-launch/types';
import { isAndroid, isiOS } from '@src/utils/util';
import { convertArticleInfo } from '@src/hooks/tracker/use-article-tracker';
import { jsBridge } from '@src/utils/js-bridge';
import videoStore from '@mobx/stores/video-store';

type VideoType = 'betpick' | 'race' | 'Masterpiece' | 'KOLBetpick' | 'betpickBar';

export const VideoJS = ({ options, onReady, autoFullscreen, contentBlock }: any) => {
    const videoRef: any = useRef(null);
    const playerRef: any = useRef(null);
    const router = useRouter();
    // console.log('--------options---------', options);

    useEffect(() => {
        // Make sure Video.js player is only initialized once
        if (!playerRef.current) {
            // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
            const videoElement = document.createElement('video-js') as any;
            videoElement.classList.add('vjs-big-play-centered');
            // videoElement.setAttribute('webkit-playsinline', 'true'); // 添加 webkit-playsinline 属性
            videoElement.setAttribute('playsinline', 'true'); // 添加 playsinline 属性
            videoRef.current.appendChild(videoElement);

            const enterFullscreen = () => {
                if (videoElement.requestFullscreen) {
                    videoElement.requestFullscreen();
                } else if (videoElement.webkitRequestFullscreen) {
                    videoElement.webkitRequestFullscreen();
                } else if (videoElement.msRequestFullscreen) {
                    videoElement.msRequestFullscreen();
                } else if (videoElement.mozRequestFullScreen) {
                    videoElement.mozRequestFullScreen();
                }
            };

            // Safari-specific fullscreen handling
            const handleFullscreenChange = () => {
                if ((document as any).webkitFullscreenElement === videoElement) {
                    videoElement.classList.add('vjs-fullscreen');
                }
            };

            // Add event listener for fullscreenchange event in Safari
            document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

            const player = (playerRef.current = videojs(videoElement, options, () => {
                videojs.log('player is ready');
                onReady && onReady(player);
                autoFullscreen && enterFullscreen();
                // autoFullscreen && videoElement.classList.add('custom-fullscreen');
            }));
            let tracked = false;

            player.on('play', () => {
                jsBridge.appCloseNativePIP();
                if (!tracked) {
                    const videoType = videoStore.GetVideoType as VideoType;

                    const typeMap = {
                        betpick: 'KOLbetpick-Betpick',
                        race: 'Leaderboard-Betpick',
                        Masterpiece: 'Leaderboard-Masterpiece',
                        KOLBetpick: 'KOLBetpick',
                        betpickBar: videoStore.setVideoTypeVal ?? '',
                    };

                    const type = typeMap[videoType] || '';

                    const videoContentBlock = videoStore.GetVideoContentBlock;
                    const raceID = videoStore.GetRaceID || '';
                    const kolName = videoStore.GetKolName;
                    console.log('videoType:' + videoType);
                    if (videoType !== 'betpickBar') {
                        nonPageTracker(
                            {
                                eventID: TrackingEvent.VideoPlay,
                                eventValue: type,
                                kolName: kolName,
                                raceID: raceID,
                                videoURL: player
                                    .currentSrc()
                                    .replace('https://streaminghkjc-a.akamaihd.net/hdflash/', ''),
                            },
                            {
                                article: videoContentBlock
                                    ? convertArticleInfo(videoContentBlock)
                                    : convertArticleInfo(contentBlock),
                            }
                        );
                    }

                    tracked = true; //  track once only
                }
            });
            // You could update an existing player in the `else` block here
            // on prop change, for example:
            // return () => {
            //     // Clean up event listeners
            //     document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            // };
        }
    }, [onReady, options, videoRef, autoFullscreen, contentBlock]);

    useEffect(() => {
        const handleRouteChange = (url: string) => {
            const player = playerRef.current;
            // 在页面离开时执行的逻辑
            console.log('页面离开:', url);

            // 如果播放器不存在或已被处理，则返回
            if (!player || player.isDisposed()) {
                return;
            }

            // 处理播放器的处置
            player.dispose();
            playerRef.current = null;

            // 退出画中画模式
            const isInPictureInPicture =
                document.pictureInPictureElement !== undefined && document.pictureInPictureElement;
            if (isInPictureInPicture) {
                document.exitPictureInPicture();
            }

            // 退出全屏模式
            if (isiOS || isAndroid) {
                if (document.fullscreenElement) {
                    document.exitFullscreen();
                }
                if ((document as any).webkitFullscreenElement) {
                    (document as any).webkitExitFullscreen();
                }
            }

            // if (player && !player.isDisposed()) {
            //     player.dispose();
            //     playerRef.current = null;
            //     if (isiOS || isAndroid || document.pictureInPictureElement) {
            //         document.exitPictureInPicture();
            //     }

            //     if (isiOS || isAndroid) {
            //         if (document.fullscreenElement) {
            //             document.exitFullscreen();
            //         }
            //         if ((document as any).webkitFullscreenElement) {
            //             (document as any).webkitExitFullscreen();
            //         }
            //     }
            // }
        };

        router.events.on('routeChangeStart', handleRouteChange);

        return () => {
            router.events.off('routeChangeStart', handleRouteChange);
        };
    }, [router.events]);

    return (
        <div data-vjs-player>
            <div
                ref={videoRef}
                className={`video-js ${autoFullscreen ? 'h-full w-[100vw]' : 'h-auto w-full'}`}
            />
        </div>
    );
};

export default VideoJS;
